import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLocalStorage } from '@frontend/hooks';

export type AgentType = 'specialized' | 'workflow';
export type AgentStatus = 'active' | 'inactive' | 'all';

export type AgentsUrlParams = {
  search?: string;
  page?: number;
  pageSize?: number;
  type?: AgentType;
  status?: AgentStatus;
};

const DEFAULT_PARAMS = {
  page: '1',
  pageSize: '20',
  status: 'active',
} as const;

export function useAgentsUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedPageSize, setStoredPageSize] = useLocalStorage('agents-pageSize-preferences', 20);

  // Initialize default params if they don't exist
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    let hasChanges = false;

    Object.entries(DEFAULT_PARAMS).forEach(([key, defaultValue]) => {
      if (!newParams.has(key)) {
        newParams.set(key, defaultValue);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const params = useMemo(
    () => ({
      search: searchParams.get('search') || '',
      page: parseInt(searchParams.get('page') || '1'),
      pageSize: storedPageSize || parseInt(searchParams.get('pageSize') || '20'),
      type: searchParams.get('type') as AgentType | undefined,
      status: searchParams.get('status') as AgentStatus | undefined,
    }),
    [searchParams, storedPageSize]
  );

  const updateParams = useCallback(
    (updates: Partial<AgentsUrlParams>) => {
      const newParams = new URLSearchParams(searchParams);

      // Set default params if they don't exist
      Object.entries(DEFAULT_PARAMS).forEach(([key, defaultValue]) => {
        if (!newParams.has(key)) {
          newParams.set(key, defaultValue);
        }
      });

      // Handle pageSize separately - persist to localStorage
      if ('pageSize' in updates && updates.pageSize !== undefined) {
        setStoredPageSize(updates.pageSize);
      }

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === '' || value === null) {
          newParams.delete(key);
        } else {
          newParams.set(key, value.toString());
        }
      });

      // Reset page to 1 when search or filters change
      if (
        ('search' in updates || 'type' in updates || 'status' in updates) &&
        !('page' in updates)
      ) {
        newParams.set('page', '1');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, setStoredPageSize]
  );

  return { params, updateParams };
}
